/* eslint-disable react/no-unescaped-entities */
/**
 * VG · Codice di Condotta — reusable long-form sections.
 *
 * Rendered in full on `/codice-di-condotta` (canonical page) and, in
 * a lighter framing, above the PA signup form as the "why + how"
 * context. Kept as three named exports so the signup can compose
 * only the first two without touching the third (which requires
 * explicit acceptance and belongs to the canonical page).
 */
import React from "react";


export function PerchePASection() {
  return (
    <section data-testid="coc-section-perche">
      <h2 className="text-2xl font-bold text-[#0A0A0A] mb-4">
        Perché diventare Property Advisor ValutaGenio
      </h2>
      <div className="space-y-4 text-[15px] leading-relaxed text-[#0A0A0A]">
        <p>
          Ogni giorno, in Italia, migliaia di proprietari si fanno una domanda
          semplice: <em>quanto vale la mia casa, e cosa potrei farci davvero?</em>
          {" "}Quasi sempre cercano la risposta da soli, prima ancora di pensare
          a un professionista. ValutaGenio nasce per essere quel primo passo —
          un'analisi seria, gratuita, costruita su dati reali (le quotazioni
          ufficiali dell'Agenzia delle Entrate, incrociate con il contesto
          reale della zona), che aiuta il proprietario a capire cosa ha e
          cosa può farne.
        </p>
        <p>
          Il vantaggio per te che lavori nel settore è preciso: entri in
          contatto con il proprietario <strong>prima</strong> che sia già
          arrivato altrove. Non stai rincorrendo un incarico già in mano a
          qualcun altro. Il proprietario ti arriva con un'analisi già fatta,
          un orientamento già chiaro sulle sue alternative, e una domanda
          concreta — non una generica ricerca di preventivi.
        </p>

        <h3 className="text-base font-semibold text-[#0A0A0A] mt-6 mb-2">
          Cosa ottieni concretamente iscrivendoti:
        </h3>
        <ul className="space-y-3 list-none pl-0">
          <BulletItem title="Lead qualificati in dashboard.">
            Ogni proprietario che completa il percorso ValutaGenio nella tua
            zona di competenza arriva nella tua scheda con l'analisi già fatta:
            dati OMI reali, alternative di progetto valutate, contesto di zona.
          </BulletItem>
          <BulletItem title="Un sistema che lavora anche mentre non sei online.">
            L'analisi, il primo orientamento, il Piano d'Azione — li costruisce
            ValutaGenio. Tu arrivi alla call già con il lavoro difficile fatto.
          </BulletItem>
          <BulletItem title="Una rete che cresce con te.">
            Ogni professionista che porti in ValutaGenio (un collega, un tecnico,
            un'impresa di fiducia) costruisce la tua rete diretta, e nel tempo
            anche quella dei livelli successivi — con un sistema di remunerazione
            a tre soglie (Bronzo, Oro, Diamante) che premia chi costruisce una
            rete ampia e attiva.
          </BulletItem>
          <BulletItem title="Nessun costo per il proprietario, nessuna barriera all'ingresso per te.">
            Il primo orientamento è sempre gratuito per chi possiede l'immobile —
            è quello che rende il sistema credibile, e quello che ti apre la
            porta con la persona giusta al momento giusto.
          </BulletItem>
        </ul>

        <p className="mt-6 text-[#0A0A0A] font-medium border-l-2 border-[#B40101] pl-4">
          Non stai comprando lead freddi. Stai entrando in un sistema che prepara
          il terreno prima di farti incontrare la persona.
        </p>
      </div>
    </section>
  );
}


export function ComeFunzionaSection() {
  const steps = [
    {
      title: "Il proprietario arriva da solo, o tramite un tuo invito.",
      body: (<>
        Un proprietario compila il percorso ValutaGenio
        (<em>CAPISCI → VALUTA → DECIDI → AGISCI</em>): racconta il suo obiettivo,
        riceve un'analisi di zona e di valore, vede le alternative di progetto
        possibili per il suo immobile (vendere, ristrutturare, mettere a reddito,
        e altre a seconda del caso), e arriva infine a un <strong>Piano d'Azione</strong> —
        la sintesi di tutto ciò che serve sapere per il prossimo passo.
      </>),
    },
    {
      title: "Il caso arriva nella tua dashboard.",
      body: (<>
        Se il proprietario sceglie di prenotare una call gratuita — con te, se sei
        il suo riferimento diretto, o con il Property Advisor più adatto assegnato
        dal sistema — il caso compare nella tua scheda lead, con tutta l'analisi
        già pronta da leggere prima di parlargli.
      </>),
    },
    {
      title: "La call è orientamento, non vendita.",
      body: (<>
        Il tuo compito nella call gratuita è aiutare il proprietario a capire meglio
        la sua situazione, rispondere alle sue domande concrete, e chiarire cosa
        serve per procedere — documenti, professionisti, passaggi pratici. Non è
        il momento di proporre il tuo servizio o negoziare un incarico.
      </>),
    },
    {
      title: "Solo dopo, se entrambi lo desiderate.",
      body: (<>
        Se al termine della call il proprietario vuole proseguire con te — un
        incarico di vendita, una consulenza tecnica, qualsiasi cosa la tua
        professione preveda — l'accordo si costruisce fuori da ValutaGenio, con
        le normali modalità della tua professione. ValutaGenio può comunque
        restare utile: se in quel momento serve un altro professionista (un
        geometra, un'impresa, un notaio) che non hai già in rete, il sistema
        ti aiuta a trovarlo.
      </>),
    },
    {
      title: "La tua rete cresce, e con lei il tuo guadagno.",
      body: (<>
        Ogni professionista che inserisci in ValutaGenio genera un contributo
        mensile finché resta abbonato, secondo il tuo livello di rete
        (Bronzo, Oro, Diamante) — a prescindere da quanti casi ti arrivano.
        È un secondo canale, parallelo al tuo lavoro immobiliare vero e proprio.
      </>),
    },
  ];
  return (
    <section data-testid="coc-section-come">
      <h2 className="text-2xl font-bold text-[#0A0A0A] mb-6">Come funziona</h2>
      <ol className="space-y-6 list-none pl-0">
        {steps.map((s, i) => (
          <li key={i} className="flex gap-4"
              data-testid={`coc-come-step-${i + 1}`}>
            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#B40101] text-white
                            font-bold flex items-center justify-center">
              {i + 1}
            </div>
            <div className="flex-1 pt-1">
              <p className="font-semibold text-[#0A0A0A] mb-1">{s.title}</p>
              <p className="text-[15px] leading-relaxed text-[#0A0A0A]">{s.body}</p>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}


export function CodiceDiCondottaSection() {
  const rules = [
    {
      title: "Orienti, non vendi — nella call gratuita.",
      body: "Il primo contatto con il proprietario, offerto tramite ValutaGenio come orientamento gratuito, serve ad aiutarlo a capire la sua situazione. Non è la sede per proporre il tuo incarico, negoziare una parcella, o esercitare pressione per una decisione immediata. Se il proprietario stesso chiede esplicitamente di proseguire con te, puoi ovviamente rispondere — ma l'iniziativa della call resta orientativa, non commerciale.",
    },
    {
      title: "L'eventuale accordo successivo resta fuori da ValutaGenio.",
      body: "Se tu e il proprietario scegliete di lavorare insieme dopo la call, l'incarico, il compenso e ogni aspetto contrattuale seguono le regole della tua professione, indipendentemente da ValutaGenio. La piattaforma non traccia, non media e non percepisce alcuna commissione su quell'accordo.",
    },
    {
      title: "Rispetti la riservatezza del caso.",
      body: "I dati e l'analisi che ricevi su un proprietario servono a orientarlo bene. Non li condividi con terzi, non li usi per scopi diversi da quel caso specifico.",
    },
    {
      title: "Sei onesto sulle tue competenze.",
      body: "La tua bio e la tua specializzazione devono riflettere realmente ciò che sai fare. Un proprietario che ti raggiunge tramite ValutaGenio deve poter contare sul fatto che chi ha di fronte è davvero competente per il suo caso — se non lo sei per un aspetto specifico, indirizzalo verso chi lo è.",
    },
    {
      title: "Rispetti la rete.",
      body: "Se presenti ValutaGenio ad altri professionisti, lo fai in modo trasparente su cosa comporta l'iscrizione, i costi e i vantaggi reali — senza promesse che il sistema non può mantenere.",
    },
    {
      title: "Tratti ogni proprietario con la stessa cura, indipendentemente dal potenziale economico del caso.",
      body: "Un piccolo immobile e un progetto di grande valore meritano lo stesso livello di attenzione nella call di orientamento. La fiducia nel sistema si costruisce anche — soprattutto — nei casi più semplici.",
    },
  ];
  return (
    <section data-testid="coc-section-regole">
      <h2 className="text-2xl font-bold text-[#0A0A0A] mb-3">
        Il Codice di Condotta
      </h2>
      <p className="text-[15px] leading-relaxed text-[#0A0A0A] mb-6">
        Accettandolo in fase di iscrizione, ti impegni a rispettare questi
        principi, che sono il fondamento della fiducia su cui ValutaGenio è
        costruito.
      </p>
      <ul className="space-y-5 list-none pl-0">
        {rules.map((r, i) => (
          <li key={i} className="border-l-2 border-[#B40101] pl-4"
              data-testid={`coc-regola-${i + 1}`}>
            <p className="font-semibold text-[#0A0A0A] mb-1">{r.title}</p>
            <p className="text-[15px] leading-relaxed text-[#0A0A0A]">{r.body}</p>
          </li>
        ))}
      </ul>
      <p className="mt-8 text-sm text-[#B40101] font-medium bg-[#B40101]/5 border border-[#B40101]/20 rounded-lg p-4"
         data-testid="coc-conseguenze">
        Il mancato rispetto di questi principi può portare alla sospensione
        dell'account e all'esclusione dal programma Property Advisor.
      </p>
    </section>
  );
}


function BulletItem({ title, children }) {
  return (
    <li className="pl-6 relative">
      <span className="absolute left-0 top-1 w-2 h-2 rounded-full bg-[#B40101]" />
      <strong className="text-[#0A0A0A]">{title}</strong> {children}
    </li>
  );
}
